import { useMemo, useRef } from 'react';

import NestedDropdown from './NestedDropdown';
import {
  PostCallAnalysisMetric,
  PostCallAnalysisSummaryApi,
} from '../Services/PostCallAnalysisApiProviderTypes';
import './PostCallAnalyticsFilters.css';

interface PostCallAnalyticsFiltersProps {
  metrics: PostCallAnalysisMetric[];
  optionSource: PostCallAnalysisSummaryApi | null;
  selected: PostCallAnalyticsFilterSelection;
  onChange: (selected: PostCallAnalyticsFilterSelection) => void;
}

export interface PostCallAnalyticsNumericRange {
  min_value: number;
  max_value: number;
}

export type PostCallAnalyticsFilterSelection = Record<
  string,
  string[] | PostCallAnalyticsNumericRange
>;

const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'True' },
  { value: 'false', label: 'False' },
];

const FILTERABLE_TYPES = ['numeric', 'categorical', 'boolean', 'list'];

const humanize = (value: string): string =>
  value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const PostCallAnalyticsFilters = ({
  metrics,
  optionSource,
  selected,
  onChange,
}: PostCallAnalyticsFiltersProps) => {
  const dynamicValuesByKey = useMemo(() => {
    const map: Record<string, string[]> = {};
    (optionSource?.categorical || []).forEach((item) => {
      map[item.metric_key] = Object.keys(item.counts || {});
    });
    return map;
  }, [optionSource]);

  const sections = useMemo(() => {
    return (metrics || [])
      .filter((m) => m.is_active !== false && FILTERABLE_TYPES.includes(m.metric_type))
      .map((metric) => {
        if (metric.metric_type === 'numeric') {
          const constraints = metric.constraints as {
            min?: number;
            max?: number;
          } | null;
          const summary = optionSource?.numeric.find((item) => item.metric_key === metric.key);
          const minValue = constraints?.min ?? summary?.min;
          const maxValue = constraints?.max ?? summary?.max;
          const min = typeof minValue === 'number' ? Math.floor(minValue) : minValue;
          const max = typeof maxValue === 'number' ? Math.ceil(maxValue) : maxValue;

          return {
            value: metric.key,
            label: metric.display_name,
            type: 'range',
            min,
            max,
            step: 1,
          };
        }

        let children: { value: string; label: string }[] = [];
        if (metric.metric_type === 'boolean') {
          children = BOOLEAN_OPTIONS;
        } else {
          const constraintValues = (metric.constraints as { values?: string[] } | null)?.values;
          const values =
            constraintValues && constraintValues.length > 0
              ? constraintValues
              : dynamicValuesByKey[metric.key] || [];
          children = values.map((v) => ({ value: v, label: humanize(v) }));
        }

        return { value: metric.key, label: metric.display_name, type: 'multi', children };
      })
      .filter((section) =>
        section.type === 'range'
          ? typeof section.min === 'number' && typeof section.max === 'number'
          : Boolean(section.children?.length),
      );
  }, [metrics, optionSource, dynamicValuesByKey]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleWrapperClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = wrapperRef.current?.querySelector('.filter-dropdown-container');
    if (container && container.contains(e.target as Node)) return;
    const button = wrapperRef.current?.querySelector('.filter-btn') as HTMLButtonElement | null;
    button?.click();
  };

  if (sections.length === 0) return null;

  return (
    <div className="pca-filters-dropdown" ref={wrapperRef} onClick={handleWrapperClick}>
      <span className="pca-filters-label">Filters</span>
      <NestedDropdown
        sections={sections}
        defaultValues={selected}
        onChange={onChange}
        externalSearchValue={undefined}
      />
    </div>
  );
};

export default PostCallAnalyticsFilters;
